import type { ReactNode } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useLocale } from "@/context/LanguageContext";
import type { AnalyticsTopEventPoint } from "@/graphql/generated";
import { ChartCard, ChartEmpty, ChartSkeleton } from "./ChartCard";
import { SuppressibleTable } from "./SuppressibleTable";
import { formatCompactNumber } from "../utils/dateRange";

interface Props {
  data: AnalyticsTopEventPoint[];
  loading?: boolean;
  action?: ReactNode;
  /** Maps a raw event key to a human-readable, localized label. */
  eventLabel: (event: string) => string;
}

export function AnalyticsTopEvents({ data, loading = false, action, eventLabel }: Props) {
  const { t } = useLocale();
  const title = t("analytics.topEvents.title");

  if (loading && data.length === 0) {
    return (
      <ChartCard title={title} action={action}>
        <ChartSkeleton />
      </ChartCard>
    );
  }
  if (data.length === 0) {
    return (
      <ChartCard title={title} action={action}>
        <ChartEmpty />
      </ChartCard>
    );
  }

  const anySuppressed = data.some((p) => p._suppressed);
  const chartData = data.map((p) => ({
    label: eventLabel(p.event),
    count: p._suppressed ? null : p.count ?? 0,
  }));

  return (
    <ChartCard title={title} action={action}>
      <ResponsiveContainer width="100%" height={Math.max(260, chartData.length * 34)}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
          <XAxis
            type="number"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
            tickFormatter={formatCompactNumber}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={150}
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
          />
          <Tooltip
            cursor={{ fill: "var(--color-muted)" }}
            contentStyle={{
              backgroundColor: "var(--color-card)",
              border: "1px solid var(--color-border)",
              borderRadius: "8px",
              color: "var(--color-foreground)",
              fontSize: 12,
            }}
            formatter={(value) => [Number(value).toLocaleString(), t("analytics.topEvents.tooltip")]}
          />
          <Bar
            dataKey="count"
            name={t("analytics.topEvents.tooltip")}
            fill="var(--color-secondary)"
            radius={[0, 4, 4, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
      <SuppressibleTable
        labelHeader={t("analytics.topEvents.eventHeader")}
        rows={data.map((p) => ({
          label: eventLabel(p.event),
          count: p.count,
          suppressed: !!p._suppressed,
        }))}
      />
      {anySuppressed && (
        <p className="mt-3 text-xs text-muted-foreground">{t("analytics.suppressionNote")}</p>
      )}
    </ChartCard>
  );
}
